import { useEffect, useId } from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import { ANSWER_REASONS, DRAFT_REASONS } from "./api"
import { useEntry, useFeedbackStore, type FeedbackRef } from "./store"
import "./feedback.css"

const NOUN = { answer: "answer", draft: "draft" } as const

/** Good answer / Needs work in the page bar. The note panel is `FeedbackPanel`, which the page puts under the bar. */
export function FeedbackThumbs({ fb }: { fb: FeedbackRef }) {
  const e = useEntry(fb)
  const { load, vote, openNote, retry } = useFeedbackStore.getState()
  useEffect(() => load(fb.app, fb.sessionId), [load, fb.app, fb.sessionId])
  const noun = NOUN[fb.target]
  return (
    <div className="nr-fb" role="group" aria-label={`Rate this ${noun}`}>
      <button
        type="button" className="nr-bar-act nr-fb-btn" aria-pressed={e.vote === 1}
        aria-label={`Good ${noun}`} title={`Good ${noun}`} onClick={() => vote(fb, 1)}
      >
        <ThumbsUp aria-hidden="true" />
      </button>
      <button
        type="button" className="nr-bar-act nr-fb-btn" aria-pressed={e.vote === -1}
        aria-label="Needs work" title="Needs work" onClick={() => vote(fb, -1)}
      >
        <ThumbsDown aria-hidden="true" />
      </button>
      <span className="nr-fb-status" aria-live="polite">
        {e.error ? (
          <>
            Couldn't save. <button type="button" className="nr-fb-link" onClick={() => retry(fb)}>Try again</button>
          </>
        ) : e.thanks === "vote" ? (
          <>
            Thanks <button type="button" className="nr-fb-link" onClick={() => openNote(fb)}>Add a note</button>
          </>
        ) : e.thanks === "sent" ? "Thanks, this helps." : null}
      </span>
    </div>
  )
}

/** Opens under the bar after a thumbs-down (or "Add a note"): optional reasons and a note, never required. */
export function FeedbackPanel({ fb, inline = false }: { fb: FeedbackRef; inline?: boolean }) {
  const e = useEntry(fb)
  const { toggleReason, setComment, send, skip } = useFeedbackStore.getState()
  const id = useId()
  if (!e.panel || !e.vote) return null
  const reasons = fb.target === "draft" ? DRAFT_REASONS : ANSWER_REASONS
  return (
    <section className={`nr nr-fb-panel${inline ? " inline" : ""}`} aria-label="Tell us more">
      <p className="nr-fb-q" id={`${id}-q`}>{e.vote === 1 ? "What worked?" : "What went wrong?"} <span>Optional</span></p>
      <div className="nr-fb-chips" role="group" aria-labelledby={`${id}-q`}>
        {reasons.map((r) => (
          <button key={r} type="button" className="nr-fb-chip" aria-pressed={e.reasons.includes(r)} onClick={() => toggleReason(fb, r)}>
            {r}
          </button>
        ))}
      </div>
      <label className="nr-fb-note" htmlFor={`${id}-t`}>
        <span className="sr-only">Your note</span>
        <textarea
          id={`${id}-t`} value={e.comment} maxLength={1000} rows={3}
          placeholder={fb.target === "draft" ? "What should the draft have said?" : "What should it have said?"}
          onChange={(ev) => setComment(fb, ev.target.value)}
        />
      </label>
      <p className="nr-fb-fine">Please don't include client names or details. Admins may review this {NOUN[fb.target]} and your note to improve Nira.</p>
      <div className="nr-fb-actions">
        <button type="button" className="nr-btn" data-variant="primary" data-size="sm" onClick={() => send(fb)}>Send</button>
        <button type="button" className="nr-btn" data-variant="quiet" data-size="sm" onClick={() => skip(fb)}>Skip</button>
      </div>
    </section>
  )
}

/** Under a finished answer: "Was this helpful?", the thumbs, and the note panel inline beneath them. */
export function FeedbackRow({ fb }: { fb: FeedbackRef }) {
  return (
    <div className="nr-fb-row">
      <div className="nr-fb-line">
        <span className="nr-fb-ask">Was this {NOUN[fb.target]} helpful?</span>
        <FeedbackThumbs fb={fb} />
      </div>
      <FeedbackPanel fb={fb} inline />
    </div>
  )
}
